import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { reviewAPI, emailAPI, feedbackAPI, monitorAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  Clock, 
  CheckCircle2, 
  Send, 
  XCircle, 
  MailCheck, 
  Play, 
  Loader2, 
  RefreshCw, 
  ArrowRight,
  Inbox,
  Settings2,
  ListCheck
} from 'lucide-react';
import { InfoTooltip } from '../components/InfoTooltip';
import { 
  StatusDistributionChart, 
  QualityScoreGauge, 
  CategoryBreakdownChart, 
  PlatformShareChart,
  AiPerformanceCard
} from '../components/AnalyticsCharts';

export default function Dashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showProgress, setShowProgress] = useState(false);
  const [jobId, setJobId] = useState<string | null>(() => localStorage.getItem('processing_job_id'));
  const [progress, setProgress] = useState({ processed: 0, total: 0, status: 'running', current_email: '' });
  const [maxEmails, setMaxEmails] = useState(50);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [monitorStatus, setMonitorStatus] = useState({ 
    running: false, 
    poll_interval: 60,
    amazon_enabled: true,
    ebay_enabled: true
  });

  // Fetch monitor status
  useQuery({
    queryKey: ['monitor-status'],
    queryFn: async () => {
      const response = await monitorAPI.getStatus();
      setMonitorStatus(response.data);
      return response.data;
    },
    refetchInterval: () => (monitorStatus.running ? 5000 : 30000),
  });

  // Fetch AI Performance & Edit Distance Analytics
  const { data: aiPerformanceData } = useQuery({
    queryKey: ['ai-performance-stats'],
    queryFn: async () => {
      const response = await reviewAPI.getAiPerformance();
      return response.data;
    },
    refetchInterval: 2000, // 2-second real-time polling
  });

  // Check for existing job on mount
  useEffect(() => {
    const savedJobId = localStorage.getItem('processing_job_id');
    if (savedJobId) {
      setJobId(savedJobId);
      monitorAPI.getProcessingStatus(savedJobId)
        .then(response => {
          setProgress(response.data);
          setShowProgress(true);
        })
        .catch(error => {
          console.error('Error fetching initial status:', error);
          localStorage.removeItem('processing_job_id');
          setShowProgress(false);
        });
    }
  }, []);

  const { data: stats } = useQuery({
    queryKey: ['review-stats'],
    queryFn: async () => {
      const response = await reviewAPI.getStats();
      return response.data;
    },
    refetchInterval: 2000, // 2-second real-time polling
  });

  // Fetch feedback stats
  const { data: feedbackStats } = useQuery({
    queryKey: ['feedback-stats'],
    queryFn: async () => {
      const response = await feedbackAPI.getStats();
      return response.data;
    },
    refetchInterval: 2000, // 2-second real-time polling
  });

  // Fetch real category distribution stats
  const { data: categoryStats } = useQuery({
    queryKey: ['category-stats'],
    queryFn: async () => {
      const response = await reviewAPI.getCategoryStats();
      return response.data;
    },
    refetchInterval: 2000, // 2-second real-time polling
  });

  // Poll for progress updates
  useEffect(() => {
    if (!jobId || (progress.status !== 'running' && progress.status !== '')) return;

    const interval = setInterval(async () => {
      try {
        const response = await monitorAPI.getProcessingStatus(jobId);
        setProgress(response.data);
        
        if (response.data.status === 'running') {
          queryClient.invalidateQueries({ queryKey: ['review-stats'] });
        }
        
        if (response.data.status === 'completed') {
          toast.success(`Processed ${response.data.processed} emails successfully`);
          queryClient.invalidateQueries({ queryKey: ['review-stats'] });
          localStorage.removeItem('processing_job_id');
          setTimeout(() => {
            setShowProgress(false);
            setJobId(null);
          }, 3000);
        } else if (response.data.status === 'failed') {
          toast.error('Failed to process emails');
          localStorage.removeItem('processing_job_id');
        } else if (response.data.status === 'cancelled') {
          localStorage.removeItem('processing_job_id');
          setTimeout(() => {
            setShowProgress(false);
            setJobId(null);
          }, 2000);
        }
      } catch (error) {
        console.error('Error fetching progress:', error);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [jobId, progress.status, queryClient]);

  const handleProcessEmails = async () => {
    try {
      const response = await emailAPI.process({ max_emails: maxEmails });
      const newJobId = response.data.job_id;
      setJobId(newJobId);
      localStorage.setItem('processing_job_id', newJobId);
      setProgress({ processed: 0, total: response.data.max_emails, status: 'running', current_email: '' });
      setShowProgress(true);
    } catch (error) {
      toast.error('Failed to start processing');
    }
  };

  const handleToggleMonitor = async () => {
    try {
      if (monitorStatus.running) {
        await monitorAPI.stop();
        toast.success('Auto-monitoring stopped');
      } else {
        await monitorAPI.start();
        toast.success('Auto-monitoring started');
      }
      queryClient.invalidateQueries({ queryKey: ['monitor-status'] });
    } catch (error) {
      toast.error('Failed to toggle monitor');
    }
  };

  const handleToggleAmazon = async () => {
    try {
      await monitorAPI.toggleAmazon();
      queryClient.invalidateQueries({ queryKey: ['monitor-status'] });
      toast.success(monitorStatus.amazon_enabled ? 'Amazon monitoring disabled' : 'Amazon monitoring enabled');
    } catch (error) {
      toast.error('Failed to toggle Amazon monitoring');
    }
  };

  const handleToggleEbay = async () => {
    try {
      await monitorAPI.toggleEbay();
      queryClient.invalidateQueries({ queryKey: ['monitor-status'] });
      toast.success(monitorStatus.ebay_enabled ? 'eBay monitoring disabled' : 'eBay monitoring enabled');
    } catch (error) {
      toast.error('Failed to toggle eBay monitoring');
    }
  };

  const handleCancelProcessing = async () => {
    if (!jobId) return;
    try {
      await monitorAPI.cancelProcessing(jobId);
      toast.success('Processing cancelled');
      localStorage.removeItem('processing_job_id');
      setShowProgress(false);
      setJobId(null);
    } catch (error) {
      toast.error('Failed to cancel processing');
    }
  };

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    await queryClient.refetchQueries();
    setTimeout(() => setIsRefreshing(false), 600);
    toast.success('Dashboard metrics updated');
  };

  const topKpis = [
    {
      title: 'Pending Review',
      value: stats?.pending_review || 0,
      subtext: 'Awaiting approval',
      icon: <Clock className="w-5 h-5 text-amber-600" />,
      bgGradient: 'from-amber-500/10 to-orange-500/5',
      borderColor: 'border-amber-200/80 hover:border-amber-300',
      badgeColor: 'bg-amber-100 text-amber-800',
      tooltip: 'Emails requiring human review before customer reply',
      onClick: () => navigate('/review'),
    },
    {
      title: 'Approved',
      value: stats?.approved || 0,
      subtext: 'Ready to send',
      icon: <CheckCircle2 className="w-5 h-5 text-blue-600" />,
      bgGradient: 'from-blue-500/10 to-indigo-500/5',
      borderColor: 'border-blue-200/80 hover:border-blue-300',
      badgeColor: 'bg-blue-100 text-blue-800',
      tooltip: 'Responses approved and waiting dispatch',
      onClick: () => navigate('/approved'),
    },
    {
      title: 'Sent',
      value: stats?.sent || 0,
      subtext: 'Delivered replies',
      icon: <Send className="w-5 h-5 text-emerald-600" />,
      bgGradient: 'from-emerald-500/10 to-teal-500/5',
      borderColor: 'border-emerald-200/80 hover:border-emerald-300',
      badgeColor: 'bg-emerald-100 text-emerald-800',
      tooltip: 'Emails successfully sent to customers',
      onClick: () => navigate('/emails?status=sent'),
    },
    {
      title: 'Rejected',
      value: stats?.rejected || 0,
      subtext: 'Declined replies',
      icon: <XCircle className="w-5 h-5 text-rose-600" />,
      bgGradient: 'from-rose-500/10 to-red-500/5',
      borderColor: 'border-rose-200/80 hover:border-rose-300',
      badgeColor: 'bg-rose-100 text-rose-800',
      tooltip: 'Drafts rejected by reviewer',
      onClick: () => navigate('/emails?status=rejected'),
    },
    {
      title: 'Total Processed',
      value: stats?.total || 0,
      subtext: 'All-time volume',
      icon: <MailCheck className="w-5 h-5 text-purple-600" />,
      bgGradient: 'from-purple-500/10 to-violet-500/5',
      borderColor: 'border-purple-200/80 hover:border-purple-300',
      badgeColor: 'bg-purple-100 text-purple-800',
      tooltip: 'Total customer emails handled by system',
      onClick: () => navigate('/emails'),
    },
  ];

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6 bg-slate-50/50 min-h-screen">
      {/* Executive Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Analytics Dashboard</h1>
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Live Monitoring
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Real-time performance metrics, AI categorization, and human-in-the-loop workflow oversight
          </p>
        </div>

        {/* Header Action Tools */}
        <div className="flex items-center gap-3">
          <Button
            onClick={handleManualRefresh}
            variant="outline"
            size="sm"
            className="flex items-center gap-2 border-gray-200 text-gray-700 hover:bg-gray-50"
            disabled={isRefreshing}
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </Button>

          {/* Quick Trigger Batch Fetch */}
          <div className="flex items-center gap-2 bg-gray-50 p-1 rounded-xl border border-gray-200">
            <input
              type="number"
              min="1"
              max="100"
              value={maxEmails}
              onChange={(e) => setMaxEmails(Number(e.target.value))}
              className="w-16 px-2 py-1 text-xs font-semibold bg-white border border-gray-200 rounded-lg text-center text-gray-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <Button
              onClick={handleProcessEmails}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium text-xs flex items-center gap-1.5 px-3 py-1.5 shadow-sm"
              disabled={showProgress}
            >
              {showProgress ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Play className="w-3.5 h-3.5 fill-current" />
              )}
              <span>Process Batch</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Active Processing Monitor Banner */}
      {showProgress && (
        <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50/40 shadow-sm animate-in fade-in duration-300">
          <CardContent className="p-5">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-3">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-blue-600 text-white rounded-xl shadow-sm">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm">Processing Email Batch...</h4>
                  <p className="text-xs text-gray-600 mt-0.5 truncate max-w-md">
                    {progress.current_email || 'Fetching and categorizing messages...'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold text-blue-700 bg-blue-100/80 px-2.5 py-1 rounded-md">
                  {progress.processed} / {progress.total} Emails
                </span>
                <Button
                  onClick={handleCancelProcessing}
                  variant="outline"
                  size="sm"
                  className="text-xs border-red-200 text-red-600 hover:bg-red-50"
                >
                  Cancel Job
                </Button>
              </div>
            </div>

            <Progress 
              value={progress.total > 0 ? (progress.processed / progress.total) * 100 : 0} 
              className="h-2 bg-blue-100"
            />
          </CardContent>
        </Card>
      )}

      {/* Top 5 KPI Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {topKpis.map((kpi) => (
          <div
            key={kpi.title}
            onClick={kpi.onClick}
            className={`group bg-white p-4 rounded-xl border ${kpi.borderColor} shadow-sm hover:shadow-md transition-all cursor-pointer flex flex-col justify-between relative overflow-hidden`}
          >
            {/* Subtle Gradient Backing */}
            <div className={`absolute inset-0 bg-gradient-to-br ${kpi.bgGradient} opacity-50 group-hover:opacity-100 transition-opacity`} />

            <div className="relative z-10 space-y-3">
              <div className="flex items-center justify-between">
                <div className="p-2 bg-white rounded-lg shadow-2xs border border-gray-100">
                  {kpi.icon}
                </div>
                <InfoTooltip content={kpi.tooltip} />
              </div>

              <div>
                <span className="text-2xl font-extrabold text-gray-900 tracking-tight">
                  {kpi.value}
                </span>
                <h3 className="text-xs font-semibold text-gray-700 mt-0.5">{kpi.title}</h3>
                <p className="text-[11px] text-gray-400 font-medium">{kpi.subtext}</p>
              </div>
            </div>

            <div className="relative z-10 pt-3 mt-2 border-t border-gray-100 flex items-center justify-between text-[11px] font-medium text-gray-500 group-hover:text-blue-600 transition-colors">
              <span>View List</span>
              <ArrowRight className="w-3.5 h-3.5 transform group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        ))}
      </div>

      {/* Analytical Visual Graphs Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Email Status Donut Chart (7 cols) */}
        <div className="lg:col-span-7">
          <StatusDistributionChart
            data={{
              pending_review: stats?.pending_review || 0,
              approved: stats?.approved || 0,
              sent: stats?.sent || 0,
              rejected: stats?.rejected || 0,
              total: stats?.total || 0,
            }}
            onSelectStatus={(statusKey) => {
              if (statusKey === 'pending_review') navigate('/review');
              else if (statusKey === 'approved') navigate('/approved');
              else navigate(`/emails?status=${statusKey}`);
            }}
          />
        </div>

        {/* AI Quality Radial Score (5 cols) */}
        <div className="lg:col-span-5">
          <QualityScoreGauge data={feedbackStats} />
        </div>
      </div>

      {/* AI Performance & Edit Distance Analytics Card */}
      <AiPerformanceCard
        editSimilarity={aiPerformanceData?.ai_edit_similarity_percentage || 94.5}
        avgResolutionMinutes={aiPerformanceData?.avg_resolution_minutes || 2.1}
        autoReplyCount={aiPerformanceData?.auto_reply_count || 0}
      />

      {/* Category & Platform Distribution Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Category Breakdown (6 cols) */}
        <div className="lg:col-span-6">
          <CategoryBreakdownChart categoryData={categoryStats} />
        </div>

        {/* Platform Share & Controls (6 cols) */}
        <div className="lg:col-span-6">
          <PlatformShareChart
            amazonEnabled={monitorStatus.amazon_enabled}
            ebayEnabled={monitorStatus.ebay_enabled}
            onToggleAmazon={handleToggleAmazon}
            onToggleEbay={handleToggleEbay}
            onToggleAll={handleToggleMonitor}
            isMonitorRunning={monitorStatus.running}
          />
        </div>
      </div>

      {/* Quick Action Command Center */}
      <Card className="border-gray-100 shadow-sm bg-white">
        <CardHeader className="pb-3 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-semibold text-gray-900">Quick Command Hub</CardTitle>
              <CardDescription className="text-xs text-gray-500">
                Direct shortcuts for review tasks, dispatch queues, and configuration
              </CardDescription>
            </div>
            <span className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-gray-100 text-gray-600">
              Shortcuts
            </span>
          </div>
        </CardHeader>
        <CardContent className="pt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {/* Action 1: Review Queue */}
          <button
            onClick={() => navigate('/review')}
            className="flex items-start gap-3.5 p-4 rounded-xl border border-gray-200/80 bg-white hover:bg-amber-50/40 hover:border-amber-300 transition-all text-left group shadow-2xs"
          >
            <div className="p-2.5 bg-amber-50 text-amber-600 rounded-xl group-hover:scale-105 transition-transform">
              <ListCheck className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-900 group-hover:text-amber-700">
                  Review Queue
                </h4>
                {stats?.pending_review > 0 && (
                  <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-amber-500 text-white">
                    {stats.pending_review}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                Evaluate AI drafted responses and approve or reject before sending.
              </p>
            </div>
          </button>

          {/* Action 2: Approved Queue */}
          <button
            onClick={() => navigate('/approved')}
            className="flex items-start gap-3.5 p-4 rounded-xl border border-gray-200/80 bg-white hover:bg-blue-50/40 hover:border-blue-300 transition-all text-left group shadow-2xs"
          >
            <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl group-hover:scale-105 transition-transform">
              <Send className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-900 group-hover:text-blue-700">
                  Approved Send
                </h4>
                {stats?.approved > 0 && (
                  <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-blue-600 text-white">
                    {stats.approved}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                Bulk dispatch approved email responses to customers via Gmail API.
              </p>
            </div>
          </button>

          {/* Action 3: Prompt Settings */}
          <button
            onClick={() => navigate('/settings')}
            className="flex items-start gap-3.5 p-4 rounded-xl border border-gray-200/80 bg-white hover:bg-purple-50/40 hover:border-purple-300 transition-all text-left group shadow-2xs"
          >
            <div className="p-2.5 bg-purple-50 text-purple-600 rounded-xl group-hover:scale-105 transition-transform">
              <Settings2 className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-gray-900 group-hover:text-purple-700">
                AI Settings
              </h4>
              <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                Configure Gemini system prompts, category rules, and API keys.
              </p>
            </div>
          </button>

          {/* Action 4: All Emails */}
          <button
            onClick={() => navigate('/emails')}
            className="flex items-start gap-3.5 p-4 rounded-xl border border-gray-200/80 bg-white hover:bg-slate-100/60 hover:border-slate-300 transition-all text-left group shadow-2xs"
          >
            <div className="p-2.5 bg-slate-100 text-slate-700 rounded-xl group-hover:scale-105 transition-transform">
              <Inbox className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-gray-900 group-hover:text-slate-800">
                Email Archive
              </h4>
              <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                Search and filter complete email history by status, category, and order ID.
              </p>
            </div>
          </button>
        </CardContent>
      </Card>
    </div>
  );
}
